import React, { useState, useEffect, useMemo } from "react";
import { useTable, usePagination, useSortBy } from "react-table";
import {
  Table,
  Row,
  Col,
  Pagination,
  PaginationItem,
  PaginationLink,
  Input,
} from "reactstrap";

function TableView({ columns, data, customPageSize, enabledemployeelist = [] }) {
  const [searchText, setSearchText] = useState("");

  // ✅ Filter rows based on search text (handles all data types)
  const filteredData = useMemo(() => {
  if (!searchText?.trim()) return data;
  const lower = searchText.toLowerCase();

  const extractText = (val) => {
    if (val == null) return "";
    if (typeof val === "string" || typeof val === "number") return val.toString();

    // ✅ Handle React elements (recursively extract children)
    if (React.isValidElement(val)) {
      const children = val.props?.children;
      if (Array.isArray(children)) return children.map(extractText).join(" ");
      return extractText(children);
    }

    // ✅ Handle arrays
    if (Array.isArray(val)) return val.map(extractText).join(" ");

    // ✅ Handle objects safely
    if (typeof val === "object") {
      try {
        return Object.values(val).map(extractText).join(" ");
      } catch {
        return "";
      }
    }

    return "";
  };

  return data.filter((row) =>
    Object.values(row).some((value) =>
      extractText(value).toLowerCase().includes(lower)
    )
  );
}, [data, searchText]);


  // ✅ React Table setup
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: filteredData,
      initialState: {
        pageIndex: 0,
        pageSize: customPageSize || 5,
      },
    },
    useSortBy,
    usePagination
  );

  // ✅ Sync custom page size
  useEffect(() => {
    if (customPageSize) {
      setPageSize(customPageSize);
    }
  }, [customPageSize, setPageSize]);

  return (
    <>
      {/* 🔍 Search box */}
      <Row className="mb-3">
        <Col sm={4}>
          <Input
            type="text"
            placeholder="Search..."
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              gotoPage(0);
            }}
          />
        </Col>
      </Row>

      {/* 🔢 Page size dropdown */}
      <Row className="mb-2" id="showPageCountInTable">
        <Col sm={2}>
          <select
            className="form-control"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              gotoPage(0);
            }}
          >
            {[5, 10, 20, 30, 40, 50].map((ps) => (
              <option key={ps} value={ps}>
                Show {ps}
              </option>
            ))}
          </select>
        </Col>
      </Row>

      {/* 🧾 Table */}
      <div className="main-div">
        <Table hover responsive {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup, idx) => (
              <tr key={idx} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th
                    key={column.id}
                    {...(column.render("Header") === "Action"
                      ? column.getHeaderProps()
                      : column.getHeaderProps(column.getSortByToggleProps()))}
                    style={{ cursor: "pointer" }}
                  >
                    <span>
                      {column.render("Header")}
                      {!["Action", "Select", "Id"].includes(
                        column.render("Header")
                      ) && (
                        <i className="fas fa-exchange-alt fa-rotate-90 ml-2"></i>
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {page.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center">
                  No data found
                </td>
              </tr>
            ) : (
              page.map((row, i) => {
                prepareRow(row);

                const isDisabled = enabledemployeelist.some(
                  (item) =>
                    item.employeeId === row.original.Id ||
                    item.Id === row.original.Id
                );

                return (
                  <tr
                    key={i}
                    {...row.getRowProps()}
                    style={{
                      backgroundColor: isDisabled ? "#f8f9fa" : "white",
                      opacity: isDisabled ? 0.6 : 1,
                    }}
                  >
                    {row.cells.map((cell, k) => (
                      <td key={k} {...cell.getCellProps()}>
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      {/* ⏩ Pagination */}
      <Row className="table-pagination align-items-center">
        <Col md={6}>
          <Pagination className="pagination">
            <PaginationItem disabled={!canPreviousPage}>
              <PaginationLink first onClick={() => gotoPage(0)} />
            </PaginationItem>
            <PaginationItem disabled={!canPreviousPage}>
              <PaginationLink previous onClick={previousPage} />
            </PaginationItem>
            <span className="mx-3">
              Page <strong>{pageIndex + 1}</strong> of {pageOptions.length}
            </span>
            <PaginationItem disabled={!canNextPage}>
              <PaginationLink next onClick={nextPage} />
            </PaginationItem>
            <PaginationItem disabled={!canNextPage}>
              <PaginationLink last onClick={() => gotoPage(pageCount - 1)} />
            </PaginationItem>
          </Pagination>
        </Col>

        <Col md={6}>
          <div
            className="input-group float-md-right"
            style={{ width: "200px" }}
          >
            <div className="input-group-prepend">
              <span className="input-group-text">Go to</span>
            </div>
            <input
              className="form-control"
              type="number"
              min={1}
              max={pageOptions.length}
              defaultValue={pageIndex + 1}
              onChange={(e) => {
                const pageNum = e.target.value ? Number(e.target.value) - 1 : 0;
                if (pageNum >= 0 && pageNum < pageCount) gotoPage(pageNum);
              }}
            />
          </div>
        </Col>
      </Row>
    </>
  );
}

// ✅ Main Memoized Wrapper
const TableWithSorPagtn = React.memo(
  ({ thead = [], trow = [], enabledemployeelist = [], pageSize = 5 }) => {
    const columns = useMemo(
      () =>
        thead.map((header) => ({
          Header: header,
          accessor: header,
        })),
      [thead]
    );

    const data = useMemo(() => trow, [trow]);

    return (
      <TableView
        columns={columns}
        data={data}
        enabledemployeelist={enabledemployeelist}
        customPageSize={pageSize}
      />
    );
  }
);

export default TableWithSorPagtn;
